import { Button, Form, Input, InputNumber, Modal, Select } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { v4 as uuiv4 } from 'uuid';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { OWButton } from '../ow_button/OWButton';
import PartEquipmentCostFormField from '../common/PartEquipmentCostFormField';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import {
	getCurrency,
	getEntityById,
	getPurchaseRequestId,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { PERMISSION_NAMES, isSupplierAllowedToAccess } from '../../utils/AuthUtils';
import { getSpotItemNameKey } from '../purchase_requests_index_page/pr_to_po_utils';

const FORM_ID = 'add-spot-item-form';
const PART_VENDORS_TC_NAME = 'PART_VENDORS_AUTOCOMPLETE';

const AddSpotItemInPRForm: FC<any> = ({
	onCancel,
	onSuccess,
	allSpotItems,
	spotItemData,
	purchaseRequests,
	loading,
	vendors,
	fetchVendors,
	fetchMultipleVendors,
	createVendor,
	currentUser,
	companyConfig,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const { setFieldsValue } = form;
	const userCurrencyId = nullSafeGetOrElse('id', getCurrency({ currentUser }), 'USD');

	const vendorId = Form.useWatch('vendorId', form) || nullSafeGet('vendorId', spotItemData);
	const unitCost = Form.useWatch('unitCost', form) || nullSafeGet('unitCost', spotItemData);
	const quantity = Form.useWatch('quantity', form) || nullSafeGet('quantity', spotItemData);

	const getVendor = useCallback(
		(id) =>
			getEntityById(id, vendors, null) ||
			(id === nullSafeGet('spotItemVendorId', spotItemData)
				? nullSafeGet('spotItemVendor', spotItemData)
				: undefined),
		[spotItemData, vendors]
	);

	const currencyId = useMemo(
		() => nullSafeGetOrElse('currencyId', getVendor(vendorId), userCurrencyId),
		[getVendor, userCurrencyId, vendorId]
	);

	const isEditingSpotItem = useMemo(
		() => !!spotItemData && allSpotItems.some((_) => _.id === spotItemData.id),
		[allSpotItems, spotItemData]
	);

	const validateUniqueName = useCallback(
		(_, value) =>
			allSpotItems.some(
				(item) =>
					item.id !== nullSafeGet('id', spotItemData) &&
					getSpotItemNameKey(item.spotItemName) === getSpotItemNameKey(value)
			)
				? Promise.reject(new Error('A spot item with this name is already added.'))
				: Promise.resolve(),
		[allSpotItems, spotItemData]
	);

	const handleSubmit = useCallback(
		(values) => {
			const spotItemVendor = getVendor(values.vendorId);
			const newRecord = {
				...values,
				spotItemName: values.spotItemName.trim(),
				spotItemVendorId: values.vendorId,
				spotItemVendor,
				spotItemCurrencyId: nullSafeGetOrElse('currencyId', spotItemVendor, userCurrencyId),
			};
			const newSpotItems = isEditingSpotItem
				? allSpotItems.map((_) => (_.id === spotItemData.id ? { ...newRecord, id: _.id } : _))
				: [...allSpotItems, { ...newRecord, id: uuiv4() }];
			onSuccess && onSuccess(newSpotItems);
		},
		[allSpotItems, getVendor, isEditingSpotItem, onSuccess, spotItemData, userCurrencyId]
	);

	const prSelectionExist = useMemo(() => !!nullSafeGet('0', purchaseRequests), [purchaseRequests]);

	const canCreateVendors = isSupplierAllowedToAccess(
		PERMISSION_NAMES.MODIFY_VENDORS,
		companyConfig,
		currentUser.roles
	);

	return (
		<Modal
			visible={true}
			title={'Add Spot Item to Purchase Request'}
			onCancel={onCancel}
			bodyStyle={{ minHeight: '400px' }}
			closable={true}
			footer={[
				<Button key="cancel" onClick={onCancel} size="large">
					Cancel
				</Button>,
				<OWButton
					key="submit"
					type="primary"
					size="large"
					htmlType="submit"
					form={FORM_ID}
					loading={loading}
				>
					{isEditingSpotItem ? 'Save Changes' : 'Add Item'}
				</OWButton>,
			]}
		>
			<Form
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={spotItemData || {}}
				id={FORM_ID}
				form={form}
			>
				{prSelectionExist ? (
					<Form.Item
						name="supplierPurchaseRequestId"
						label="Purchase Request"
						rules={[{ required: true, message: 'Please select a purchase request.' }]}
					>
						<Select disabled={purchaseRequests.length === 1}>
							{purchaseRequests.map((pr) => (
								<Select.Option value={pr.id} key={pr.id}>
									<div>{`${getPurchaseRequestId(pr)} - ${pr.name}`}</div>
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				) : null}
				<Form.Item
					name="spotItemName"
					label="Item Name"
					rules={[
						{ required: true, whitespace: true, message: 'Please add an item name.' },
						{ validator: validateUniqueName },
					]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="quantity"
					label="Quantity"
					rules={[
						{ required: true, message: 'Please add quantity.' },
						{ type: 'integer', min: 1, message: 'Quantity must be a whole number of at least 1' },
					]}
				>
					<InputNumber style={{ width: '100%' }} />
				</Form.Item>
				<Form.Item
					name="vendorId"
					label="Vendor"
					rules={[{ required: true, message: 'Please select a vendor.' }]}
				>
					<OWAsyncSelect
						disabled={prSelectionExist}
						stateSlice={vendors}
						valueAccessor={(el) => el.id}
						targetCollectionName={PART_VENDORS_TC_NAME}
						createNewEntity={
							canCreateVendors ? (name) => createVendor({ name, currencyId: 'USD' }) : undefined
						}
						fetchData={(searchText, targetCollectionName) =>
							fetchVendors({ name: searchText }, targetCollectionName)
						}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleVendors(ids, targetCollectionName);
						}}
						placeholder="Select Vendor"
						renderRecord={(vendor) => (
							<Select.Option key={vendor.id} value={vendor.id}>
								{vendor.name}
							</Select.Option>
						)}
					/>
				</Form.Item>
				<PartEquipmentCostFormField
					entityId={undefined}
					unitCost={unitCost}
					entityName="partId"
					vendorId={vendorId}
					fetchMethod={undefined}
					setFieldsValue={setFieldsValue}
					form={form}
					currencyId={currencyId}
					quantity={quantity}
					displayTotalCostOnlyWithValue
				/>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	vendors: state.vendors,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchVendors: (params, targetCollectionName) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.readLite(
				{ ...(params || {}), isActive: true, no_pagination: true },
				targetCollectionName
			)
		),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	createVendor: (entity) => dispatch(vendorsRestCrudThunksForSupplier.create(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddSpotItemInPRForm);
